import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Form, Input, Button, Label, QueryErrorAlert } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError } from '@/services/api';
import api from '@/services/api';
import { serviceKeys, serviceItemsQueryKeyRoot } from '../hooks/useServicesQuery';
import { planServicesQueryKeyRoot } from '../hooks/usePlanServiceMutations';
import { serviceKeys as salesJourneyServiceKeys } from '@/features/service-plans/hooks/useServicesQuery';
import { ItemCombobox } from './ItemCombobox';
import { ServiceItemDataTable } from './ServiceItemDataTable';
import type { ServiceItemRecord } from './ServiceItemDataTable';

interface ServiceItemPanelProps {
    serviceId: string;
    /** Total-mode services price the whole visit; items are descriptive only and
     *  always attach at £0 (D5/D6), so the price field is not shown. */
    costMode?: 'total' | 'breakdown';
}

const UNIT_PRICE_PATTERN = /^\d+(\.\d{1,4})?$/;
const ZERO_PRICE = '0';

const addItemSchema = z.object({
    item_id: z.string().min(1, 'Select an item'),
    unit_quantity: z.number().int().min(1, 'Quantity must be at least 1'),
    unit_price_net: z.string().regex(UNIT_PRICE_PATTERN, 'Enter an amount of 0 or more with up to 4 decimal places'),
});

type AddItemInput = z.input<typeof addItemSchema>;
type AddItemOutput = z.output<typeof addItemSchema>;

const listQueryKey = (serviceId: string) => [...serviceItemsQueryKeyRoot, serviceId];

export const ServiceItemPanel: React.FC<ServiceItemPanelProps> = ({ serviceId, costMode }) => {
    const isTotalMode = costMode === 'total';
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);

    const {
        data: serviceItemsResponse,
        isLoading: isItemsLoading,
        isError: isItemsError,
        isFetching: isItemsFetching,
        error: itemsError,
        refetch: refetchItems,
    } = useQuery({
        queryKey: listQueryKey(serviceId),
        queryFn: async () => (await api.get(`/smr/services/${serviceId}/items`)).data,
        enabled: Boolean(serviceId),
    });
    const serviceItems: ServiceItemRecord[] = ((serviceItemsResponse?.data ?? serviceItemsResponse) as ServiceItemRecord[]) ?? [];

    // The promise is returned so a mutation stays pending until the items list, the service
    // subtree, every plan's services list and the sales-journey cache have all reconciled.
    const invalidate = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: listQueryKey(serviceId) }),
            queryClient.invalidateQueries({ queryKey: serviceKeys.all }),
            queryClient.invalidateQueries({ queryKey: planServicesQueryKeyRoot }),
            queryClient.invalidateQueries({ queryKey: salesJourneyServiceKeys.all }),
        ]);

    const removeMutation = useMutation({
        mutationFn: async (itemId: string) => {
            await api.delete(`/smr/services/${serviceId}/items/${itemId}`);
        },
        onSuccess: invalidate,
    });

    const updateMutation = useMutation({
        mutationFn: async ({ itemId, qty, unitPriceNet }: { itemId: string; qty: number; unitPriceNet: string }) => {
            await api.put(`/smr/services/${serviceId}/items/${itemId}`, {
                unit_quantity: qty,
                unit_price_net: unitPriceNet,
            });
        },
        onSuccess: invalidate,
    });

    const { handleSubmit, control, reset, formState: { isSubmitting } } = useForm<AddItemInput, unknown, AddItemOutput>({
        resolver: zodResolver(addItemSchema),
        defaultValues: { item_id: '', unit_quantity: 1, unit_price_net: ZERO_PRICE },
    });

    const createMutation = useMutation({
        mutationFn: async (data: AddItemOutput) => {
            await api.post(`/smr/services/${serviceId}/items`, data);
        },
        onSuccess: async () => {
            await invalidate();
            reset();
            setShowForm(false);
        },
    });

    const handleCancel = () => {
        reset();
        createMutation.reset();
        setShowForm(false);
    };

    return (
        <div className="space-y-6">
            {isItemsLoading ? (
                <ServiceItemDataTable
                    data={serviceItems}
                    onRemove={(itemId) => removeMutation.mutate(itemId)}
                    onUpdate={(itemId, qty, unitPriceNet) => updateMutation.mutateAsync({ itemId, qty, unitPriceNet: isTotalMode ? '0.00' : unitPriceNet })}
                    isLoading
                    isTotalMode={isTotalMode}
                />
            ) : isItemsError ? (
                <QueryErrorAlert
                    message={isApiError(itemsError) ? itemsError.message : 'Failed to load items.'}
                    onRetry={() => void refetchItems()}
                    isRetrying={isItemsFetching}
                />
            ) : serviceItems.length === 0 ? (
                <p className="text-mp-muted text-sm">No items attached.</p>
            ) : (
                <ServiceItemDataTable
                    data={serviceItems}
                    onRemove={(itemId) => removeMutation.mutate(itemId)}
                    onUpdate={(itemId, qty, unitPriceNet) => updateMutation.mutateAsync({ itemId, qty, unitPriceNet: isTotalMode ? '0.00' : unitPriceNet })}
                    removingItemId={removeMutation.isPending ? (removeMutation.variables ?? null) : null}
                    isTotalMode={isTotalMode}
                />
            )}

            <PermissionGuard permission={{ resource: 'smr', action: 'service.edit' }}>
                <div className="border-t border-mp-border pt-6 max-w-2xl">
                    {showForm ? (
                        <>
                            {createMutation.isError && (
                                <Alert variant="error" className="mb-4">
                                    {isApiError(createMutation.error)
                                        ? createMutation.error.message
                                        : 'Failed to add item.'}
                                </Alert>
                            )}

                            <Form className="space-y-4 max-w-lg" onSubmit={handleSubmit((data) => createMutation.mutate(isTotalMode ? { ...data, unit_price_net: ZERO_PRICE } : data))}>
                                <div className={`grid grid-cols-1 gap-3 ${isTotalMode ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                                    <div>
                                        <Label htmlFor="item_id">Item</Label>
                                        <Controller
                                            name="item_id"
                                            control={control}
                                            render={({ field, fieldState: { error } }) => (
                                                <>
                                                    <ItemCombobox
                                                        id="item_id"
                                                        value={field.value}
                                                        onChange={(id) => field.onChange(id)}
                                                        excludeIds={serviceItems.map((i) => i.id)}
                                                        aria-invalid={!!error}
                                                        aria-describedby={error ? 'item_id-hint' : undefined}
                                                    />
                                                    {error?.message && (
                                                        <p role="alert" id="item_id-hint" className="mt-1 text-xs text-mp-error">
                                                            {error.message}
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="unit_quantity">Qty</Label>
                                        <Controller
                                            name="unit_quantity"
                                            control={control}
                                            render={({ field, fieldState: { error } }) => (
                                                <Input
                                                    id="unit_quantity"
                                                    type="number"
                                                    min="1"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                                                    onBlur={field.onBlur}
                                                    error={!!error}
                                                    hint={error?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                    {!isTotalMode && (
                                        <div>
                                            <Label htmlFor="unit_price_net">Unit Net (£)</Label>
                                            <Controller
                                                name="unit_price_net"
                                                control={control}
                                                render={({ field, fieldState: { error } }) => (
                                                    <Input
                                                        id="unit_price_net"
                                                        type="number"
                                                        min="0"
                                                        step={0.0001}
                                                        value={field.value}
                                                        onChange={(e) => field.onChange(e.target.value)}
                                                        onBlur={field.onBlur}
                                                        error={!!error}
                                                        hint={error?.message}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button size="sm" disabled={isSubmitting || createMutation.isPending}>
                                        {createMutation.isPending ? 'Adding...' : 'Add Item'}
                                    </Button>
                                    <Button size="sm" type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting || createMutation.isPending}>
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        </>
                    ) : (
                        <div>
                            <h3 className="font-semibold mb-2">Add Item</h3>
                            <div className="flex items-start gap-4 justify-between">
                                <p className="text-sm text-mp-muted">
                                    {isTotalMode
                                        ? 'Attach a descriptive line to this service. Total-cost services price the whole visit, so items carry no price of their own.'
                                        : 'Attach a part or labour line to this service, specifying the quantity and unit net price.'}
                                </p>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant="primary"
                                    onClick={() => setShowForm(true)}
                                    className="whitespace-nowrap"
                                >
                                    Add Item
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </PermissionGuard>
        </div>
    );
};
